import * as Yup from 'yup'
import { CreateErrorOptions } from 'yup/lib/util/createValidation'
import ValidationError from 'yup/lib/ValidationError'
import { isEmpty, capitalize } from 'lodash'
import { TransferInitiationAssetType } from '../../api/types'
import { ALPHANUMERIC, toIsoCalendarDate } from '../../utils'

const lengthTest = (
  value: string | undefined,
  maxLength: number,
  fieldPath: string,
  createError: (params?: CreateErrorOptions) => ValidationError
) =>
  value && value.length > maxLength
    ? createError({
        message: `Maximum length is ${maxLength} characters`,
        path: fieldPath,
      })
    : true

export const initiateTransferSchema = () =>
  Yup.object().shape({
    transferType: Yup.string().required('Required'),
    contraParty: Yup.object().shape({
      account: Yup.string()
        .required('Required')
        .test('account-length', function (value) {
          return lengthTest(value, 20, 'contraParty.account', this.createError)
        }),
      participant: Yup.object().shape({
        participantNumber: Yup.string()
          .required('Required')
          .test('participantId-length', function (value) {
            return value?.length !== 4 || isNaN(Number(value))
              ? this.createError({
                  message: `Must be 4 digit number`,
                  path: 'contraParty.participant.participantNumber',
                })
              : true
          }),
      }),
      accountTitle: Yup.string()
        .required('Required')
        .test('contraParty.accountTitle-length', function (value) {
          return lengthTest(
            value,
            60,
            'contraParty.accountTitle',
            this.createError
          )
        }),
      comment: Yup.string().test(
        'contraParty.comment-length',
        function (value) {
          return lengthTest(value, 255, 'contraParty.comment', this.createError)
        }
      ),
    }),
    clientReferenceId: Yup.string().test(
      'clientReferenceId-length',
      function (value) {
        return lengthTest(value, 255, 'clientReferenceId', this.createError)
      }
    ),
    cash: Yup.array()
      .of(
        Yup.object().shape({
          rowId: Yup.string(),
          type: Yup.string(),
          amount: Yup.number(),
          isoCurrencyCode: Yup.string(),
        })
      )
      .test('cash-assets-complete', function (cashAssetRows) {
        const badRows = cashAssetRows
          ?.filter(entry => !entry.amount || entry.amount <= 0)
          .map(
            entry =>
              `${entry.rowId}-${
                entry.amount !== undefined && entry.amount <= 0
                  ? 'Value must be positive'
                  : 'Required'
              }`
          )
        return (
          isEmpty(badRows) ||
          this.createError({ message: badRows?.join(';'), path: 'cash' })
        )
      }),
    options: Yup.array().of(
      Yup.object().shape({
        rowId: Yup.string(),
        isShort: Yup.string().nullable().required('Required'),
        symbol: Yup.string().nullable().required('Required'),
        amount: Yup.number()
          .nullable()
          .positive('Value must be positive')
          .integer('Value must be a whole number')
          .required('Required'),
        // mixed, not string: a picked date arrives as a Date, which
        // Yup.string() would cast to Date.toString() before the test runs
        contractDate: Yup.mixed<Date | string>()
          .transform(value => (value === '' ? undefined : value))
          .nullable()
          .required('Required')
          .test(
            'isValidContractDate',
            'Must be a valid date (MM/DD/YYYY)',
            value => value == null || toIsoCalendarDate(value) !== undefined
          ),
        strikePrice: Yup.number()
          .nullable()
          .positive('Value must be positive')
          .required('Required'),
        put: Yup.string().nullable().required('Required'),
      })
    ),
    assets: Yup.array().of(
      Yup.object().shape({
        rowId: Yup.string(),
        isShort: Yup.boolean().required('Required'),
        identifier: Yup.string()
          .required('Required')
          .when('assetType', {
            is: TransferInitiationAssetType.Cusip,
            then: Yup.string()
              .length(9, `Must be valid ${TransferInitiationAssetType.Cusip}`)
              .test(
                'isAlphanumeric',
                `Must be valid ${TransferInitiationAssetType.Cusip}`,
                value => ALPHANUMERIC.test(value!)
              ),
          })
          .when('assetType', {
            is: TransferInitiationAssetType.Symbol,
            then: Yup.string()
              .max(
                7,
                `Must be valid ${capitalize(
                  TransferInitiationAssetType.Symbol
                )}`
              )
              .test(
                'isAlphanumeric',
                `Must be valid ${capitalize(
                  TransferInitiationAssetType.Symbol
                )}`,
                value => ALPHANUMERIC.test(value!)
              ),
          })
          .when('assetType', {
            is: TransferInitiationAssetType.Sedol,
            then: Yup.string()
              .length(7, `Must be valid ${TransferInitiationAssetType.Sedol}`)
              .test(
                'isAlphanumeric',
                `Must be valid ${TransferInitiationAssetType.Sedol}`,
                value => ALPHANUMERIC.test(value!)
              ),
          })
          .when('assetType', {
            is: TransferInitiationAssetType.Isin,
            then: Yup.string()
              .length(12, `Must be valid ${TransferInitiationAssetType.Isin}`)
              .test(
                'isAlphanumeric',
                `Must be valid ${TransferInitiationAssetType.Isin}`,
                value => ALPHANUMERIC.test(value!)
              ),
          }),
        amount: Yup.number()
          .required('Required')
          .positive('Value must be positive'),
        assetType: Yup.string(),
      })
    ),
  })
